import {
  DeleteOutlined,
  EyeOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons'
import Box from '@shared/Box'
import env from '@shared/env'
import { isMobile } from '@shared/utils/browser-detect'
import { uuid } from '@shared/utils/webHelper'
import theme from '@theme'
import { Modal, Upload, message } from 'antd'
import { RcFile, UploadProps } from 'antd/lib/upload'
import { UploadFile } from 'antd/lib/upload/interface'
import moment from 'moment'
import { rgba } from 'polished'
import { compose, is, last, pathOr, split } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import ImageViewerModal from './ImageViewerModal'

export type UploadedFile = UploadFile & {
  file?: RcFile
}

type Props = {
  value?: string | object | null
  readOnly?: boolean
  previwVisible?: boolean
  changeValueProp?: string

  loading?: boolean
  disabled?: boolean
  pictureSeq?: number
  onPreview?: (image: any) => any
  remove?: (pictureSeq: number) => void
  render?: () => React.ReactNode
  onChange?: (
    imageInfo?: {
      hash: string
      previewUrl: string
      path: string
    },
    pictureSeq?: number
  ) => void
  onUpdate?: ({ file }: any, pictureSeq?: number, isNew?: boolean) => void
  onRemove?: (seq?: number) => void
  maxImgSize: number
  maxWidth?: number
  maxHeight?: number
  isModalMode?: boolean
  modalData?: Array<any>
  title?: string
} & UploadProps

// const MAX_WIDTH = 1024
// const MAX_HEIGHT = 768
const MIME_TYPE = 'image/jpeg'
const QUALITY = 1

function ImageUpload({
  value,
  readOnly = false,
  pictureSeq,
  loading,
  render,
  onRemove,
  onUpdate,
  maxImgSize: _maxImgSize,
  maxWidth = 1024,
  maxHeight = 768,
  isModalMode = false,
  modalData = [],
  title = '',
  ...props
}: Props) {
  const { t } = useTranslation()

  const maxImgSize = React.useMemo(() => _maxImgSize, [_maxImgSize])

  const [previwVisible, setPreviewVisible] = React.useState(props.previwVisible)

  const handlePreview = (evt?: React.MouseEvent<HTMLElement>) => {
    if (evt) {
      evt.preventDefault()
      evt.stopPropagation()
    }

    const { onPreview } = props

    if (onPreview) {
      onPreview(
        pathOr<any>(value ? `${env.apiBaseUrl}${value}` : null, ['url'], value)
      )
    } else {
      setPreviewVisible(prev => !prev)
    }
  }

  const handleRemove = (evt: React.MouseEvent<HTMLElement>) => {
    if (evt) {
      evt.stopPropagation()
      evt.preventDefault()
    }
    onRemove && onRemove(pictureSeq)
  }

  const renderImg = React.useCallback(
    (children: React.ReactNode) => {
      if (!is(String, value)) {
        return children
      }

      return (
        <a
          download={compose(pathOr('', []), last, split('/'))(value)}
          href={pathOr('', ['props', 'src'], children)}
          css={{ cursor: 'default' }}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            console.log('[[renderImg]click]')
          }}>
          {children}
        </a>
      )
    },
    [value]
  )

  const customRequest = ({ file, data }: any) => {
    toBase64(file, maxWidth, maxHeight).then(val => {
      let newFile = {
        uid: uuid(),
        base64: val,
        name: file.name,
        status: 'done',
        file,
      }

      if (onUpdate) {
        onUpdate({ ...newFile }, pictureSeq)
      }
    })
  }

  const isMobileDevice = React.useMemo(() => isMobile(), [])

  const url = React.useMemo(() => {
    const temp = pathOr<any>(null, ['url'], value)

    return temp?.startsWith('http')
      ? `${temp}?timestamp=${moment().valueOf()}`
      : temp
  }, [value])

  return (
    <>
      {!isMobileDevice &&
        previwVisible &&
        url &&
        (isModalMode ? (
          <ImageViewerModal
            item={modalData}
            title={title}
            isEdit={!readOnly}
            onCancel={handlePreview}
            onRemove={onRemove}
            pictureSeq={pictureSeq}
          />
        ) : (
          <StyledModal
            visible
            maskStyle={{ background: rgba(0, 0, 0, 0.7) }}
            width="100%"
            style={{ top: 30 }}
            bodyStyle={{
              background: 'transparent',
              display: 'flex',
              justifyContent: 'center',
            }}
            footer={null}
            onCancel={handlePreview}>
            {renderImg(
              <img
                onClick={handlePreview}
                alt="example"
                style={{
                  maxWidth: '100%',
                  width: 'auto',
                  display: 'block',
                  boxShadow:
                    '0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)',
                }}
                src={url}
              />
            )}
          </StyledModal>
        ))}
      <StyledUpload
        {...props}
        accept="image/*"
        listType="picture-card"
        openFileDialogOnClick={!readOnly}
        multiple={false}
        showUploadList={false}
        customRequest={customRequest}
        beforeUpload={file => {
          const checked = beforeUpload(file, maxImgSize)
          return checked
        }}
        onChange={() => {}}>
        {url ? (
          <Box position="relative">
            {!isMobileDevice &&
              !readOnly &&
              renderImg(
                <Box
                  className="image-upload__overlay"
                  position="absolute"
                  top={0}
                  right={0}
                  width="100%"
                  height="100%"
                  color="#fff"
                  fontSize="16px">
                  <EyeOutlined
                    type="eye"
                    style={{
                      position: 'relative',
                      top: '35%',
                      marginRight: 10,
                    }}
                    onClick={handlePreview}
                  />
                  <DeleteOutlined
                    type="delete"
                    style={{
                      position: 'relative',
                      top: '35%',
                    }}
                    onClick={handleRemove}
                  />
                </Box>
              )}
            <img
              src={url}
              alt="avatar"
              onClick={handlePreview}
              style={{ height: 84, width: 120 }}
            />
          </Box>
        ) : render ? (
          render()
        ) : (
          <div>
            {!readOnly && !loading ? (
              <PlusCircleOutlined
                type={'plus'}
                css={{ color: theme.imageuploader.iconColor }}
              />
            ) : (
              <LoadingOutlined spin={loading} className="text-2xl" />
            )}
            <div className="ant-upload-text">
              {readOnly ? t('common:not upload photo') : '' /* '上傳照片' */}
            </div>
          </div>
        )}
      </StyledUpload>
    </>
  )
}

const StyledModal = styled(Modal)`
  animation-duration: 0s !important;
  transition: none !important;
  &.ant-modal {
    .ant-modal-content {
      background: transparent;
    }
    .ant-modal-close-x {
      margin-right: 5px;
      margin-top: -23px;
      color: #fff;
      font-size: 28px;
    }
  }
`

const StyledUpload = styled(Upload)`
  animation: none !important;
  transition: none !important;
  .ant-upload.ant-upload-select-picture-card {
    width: 120px;
    height: 84px;
  }
  &.ant-upload-picture-card-wrapper {
    width: auto;
  }
  &.is--xs.ant-upload-picture-card-wrapper {
    width: 100%;
    height: 64px;
  }
  &.is--sm .ant-upload.ant-upload-select-picture-card,
  &.is--xs .ant-upload.ant-upload-select-picture-card {
    background: transparent;
    border: 1px solid ${p => p.theme.primary};
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    .ant-upload {
      color: ${p => p.theme.primary};
      padding: 0;
    }
  }
  &.is--sm .ant-upload.ant-upload-select-picture-card {
    width: 64px;
    height: 64px;
    img {
      width: 61px !important;
      height: 61px !important;
    }
  }
  &.is--xs .ant-upload.ant-upload-select-picture-card {
    width: 100%;
    height: 100%;
    img {
      width: 68px !important;
      height: 55px !important;
    }
  }
  .ant-upload-list-picture-card .ant-upload-list-item:hover,
  .ant-upload.ant-upload-select-picture-card:hover {
    background: ${p => p.theme.imageuploader.bg};
    border-color: ${p => p.theme.imageuploader.boderColor};
  }
  .ant-upload-list-picture-card .ant-upload-list-item,
  .ant-upload.ant-upload-select-picture-card {
    background: ${p => p.theme.imageuploader.bg};
    border-color: ${p => p.theme.imageuploader.boderColor};
  }
  .ant-upload-list-picture-card .ant-upload-list-item-info::before {
    background: rgba(216, 216, 216, 0);
    border-color: none;
  }
  .ant-upload-list-picture-card
    .ant-upload-list-item:hover
    .ant-upload-list-item-info::before {
    opacity: 0;
  }
  .ant-upload-list-picture-card .ant-upload-list-item-info::before,
  .ant-upload-list-item-info {
    animation: none !important;
    transition: none !important;
  }
  .ant-upload-list-picture-card .ant-upload-list-item-actions {
    .anticon {
      color: ${p => p.theme.primary};
    }
    .anticon:hover {
      color: ${p => p.theme.secondary};
    }
  }
  .image-upload__overlay {
    transition: all 0.2s ease-in;
    opacity: 0;
  }
  .image-upload__overlay:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.6);
  }
`

function calculateScale({ img, maxWidth, maxHeight }: any) {
  let width = img.width
  let height = img.height

  // calculate the width and height, constraining the proportions
  if (width > height) {
    if (width > maxWidth) {
      height = Math.round((height * maxWidth) / width)
      width = maxWidth
    }
  } else {
    if (height > maxHeight) {
      width = Math.round((width * maxHeight) / height)
      height = maxHeight
    }
  }
  return [width, height]
}

function toBase64(file: File, maxWidth: number, maxHeight: number) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()

    if (file.type === 'image/gif') {
      fileReader.addEventListener('load', () => resolve(fileReader.result))
    } else {
      const url = window.URL.createObjectURL(file)

      const img = new Image()
      img.src = url

      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        console.log('Cannot load image')
      }

      img.onload = () => {
        URL.revokeObjectURL(img.src)
        const [newWidth, newHeight] = calculateScale({
          img,
          maxWidth,
          maxHeight,
        })
        const canvas = document.createElement('canvas')
        canvas.width = newWidth
        canvas.height = newHeight
        const ctx = canvas.getContext('2d')
        ctx?.drawImage(img, 0, 0, newWidth, newHeight)
        var smBase64 = canvas.toDataURL(MIME_TYPE, QUALITY)

        resolve(smBase64)
      }
    }
  })
}

const beforeUpload = (file: RcFile, size: number) => {
  const isLt2M = file.size / 1024 / 1024 < size
  if (!isLt2M) {
    message.error({
      className: 'g-upload-error',
      content: `檔案不可超過 ${size}MB`,
      type: 'error',
    })
  }
  return isLt2M
}

export default React.memo(ImageUpload)
